import type { Reminder } from '@nexo/types';
import { IconButton, useToast } from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { formatDay, formatTime } from '../../lib/format';
import { errorMessage } from '../../lib/forms';
import { keys } from '../../lib/queries';

/** The person's pending «Recuérdamelo» in this team (shown on the Saved page). */
export function RemindersList({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: keys.reminders(teamId),
    queryFn: () => api.reminders.list(teamId),
    refetchInterval: 60_000,
  });
  const remove = useMutation({
    mutationFn: api.reminders.remove,
    onSuccess: (_, id) =>
      queryClient.setQueryData<Reminder[]>(keys.reminders(teamId), (items) =>
        items?.filter((item) => item.id !== id),
      ),
    onError: (err) =>
      toast({ title: t('No se pudo borrar'), description: errorMessage(err), tone: 'error' }),
  });

  if (!list.data?.length) return null;
  return (
    <section className="border-b border-border px-6 py-4">
      <h2 className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-subtle uppercase">
        <AlarmClock className="size-3.5" /> {t('Recordatorios pendientes')}
      </h2>
      <ul className="flex flex-col gap-1">
        {list.data.map((reminder) => (
          <li
            key={reminder.id}
            className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-surface-2"
          >
            <span className="w-36 shrink-0 text-xs font-medium text-accent">
              {formatDay(reminder.remindAt)} · {formatTime(reminder.remindAt)}
            </span>
            {reminder.channelId ? (
              <Link
                to={`/app/t/${teamId}/c/${reminder.channelId}${reminder.messageId ? `?m=${reminder.messageId}` : ''}`}
                className="min-w-0 flex-1 truncate text-sm hover:text-accent"
              >
                {reminder.text || t('(sin texto)')}
              </Link>
            ) : (
              <span className="min-w-0 flex-1 truncate text-sm">{reminder.text}</span>
            )}
            <IconButton
              label={t('Borrar recordatorio')}
              size="sm"
              onClick={() => remove.mutate(reminder.id)}
            >
              <X />
            </IconButton>
          </li>
        ))}
      </ul>
    </section>
  );
}
